import { useEffect, useState } from "react"
import { useSession } from "../../auth/session"
import { Link, useSearchParams } from "react-router-dom"
import Api from "../../api/api"
import { Sku, License, NfaOrderStatus, Order, Product } from "../../api/models"
import { useStoreSku } from "../../redux/storeSlice"
import SkuImage from "../widgets/SkuImage"
import SkuName from "../widgets/SkuName"
import ProductLinks from "../widgets/ProductLinks"

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 2 * 60 * 1000

// LemonSqueezy redirects here after payment with ?skuId=<uuid>&
// purchaseId=<uuid>, both set by BuyPage. The purchaseId uniquely identifies
// this checkout: it traveled through the LemonSqueezy custom checkout data
// onto the order row the webhook creates. Fulfillment is asynchronous, so
// this page polls GET /orders?purchaseId=... until that exact order appears,
// then shows the licenses granted by it (license.refId points back at the
// order). For an order the purchased sku comes from the order row itself, so
// skuId only feeds the product screenshot and is optional — checkouts minted
// before the skuId cutover still confirm via purchaseId.
//
// Point-reward claims land here too, with ?rewardClaimId=<pointSpending uuid>
// instead of purchaseId. Claims are fulfilled synchronously before the
// redirect, so a single licenses fetch (license.refId points back at the
// point spending) confirms — no order exists, marked by order: null. There is
// no spending row on the page, so for a claim skuId also names the claimed
// sku whose installer the confirmed card links.
//
// The confirmed card links ONE installer: the purchased sku's. A bundle's
// installer covers every product it grants, and the per-product skus of the
// licenses response would include every bundle that also claims the product.
type PurchaseState =
  | { phase: "polling" }
  | { phase: "confirmed", order: Order | null, licenses: License[], products: Product[], skus: Sku[] }
  | { phase: "timeout" }

export default function PurchaseSuccessPage() {
  const [searchParams] = useSearchParams()
  const skuId = searchParams.get("skuId")
  const purchaseId = searchParams.get("purchaseId")
  const rewardClaimId = searchParams.get("rewardClaimId")

  if (!skuId && !purchaseId && !rewardClaimId)
    return (
      <div className="page">
        <div className="empty-state-block">
          <span className="empty-state-icon">&#128722;</span>
          <span>No purchase to show</span>
          <span className="empty-state-hint">This page confirms a purchase, but no product was specified</span>
          <Link to="/licenses" className="card-link" style={{ marginTop: 12 }}>View Licenses</Link>
        </div>
      </div>
    )

  return <PurchaseSuccessContent skuId={skuId} purchaseId={purchaseId} rewardClaimId={rewardClaimId} />
}

type ConfirmedState = Extract<PurchaseState, { phase: "confirmed" }>

// The sku that was bought (order row) or claimed (URL skuId), resolved from
// the getLicenses skus — those carry the installer URLs, incl. the user's
// test builds. Undefined when it isn't there (hand-edited URL, or a sku that
// grants no license, e.g. topup-only).
function purchasedSku(state: ConfirmedState, urlSkuId: string | null): Sku | undefined {
  const skuId = state.order !== null ? state.order.skuId : urlSkuId
  if (skuId === null)
    return undefined
  return state.skus.find((sku) => sku.id === skuId)
}

function hasInstaller(sku: Sku): boolean {
  return !!(sku.downloadMacUrl || sku.downloadWinUrl || sku.testDownloadMacUrl || sku.testDownloadWinUrl)
}

function ConfirmedCard({ state, skuId }: { state: ConfirmedState, skuId: string | null }) {
  // order === null marks a point-reward claim — no order row exists
  const grant = state.order !== null
    ? <>Your <Link to={"/orders/" + state.order.id} className="card-link">order</Link> is confirmed</>
    : <>Your reward is claimed</>
  const sku = purchasedSku(state, skuId)
  const installer = sku !== undefined && hasInstaller(sku) ? sku : undefined

  return (
    <div className="card" style={{ marginTop: 24 }}>
      {state.licenses.length > 0 ? (
        <>
          <div className="card-detail">
            {grant} and your{" "}
            {state.licenses.length === 1
              ? <><Link to={"/licenses/" + state.licenses[0].id} className="card-link">license</Link> is</>
              : <><Link to="/licenses" className="card-link">licenses</Link> are</>}
            {" "}ready{installer !== undefined ? " — download and install to get started:" : "."}
          </div>
          {installer !== undefined && (
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16, marginTop: 12 }}>
              <ProductLinks skus={[installer]} showProductPage={false} />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="card-detail">{grant} — everything has been added to your account.</div>
          <div className="card-links">
            <Link to="/licenses" className="card-link">View Licenses</Link>
            <Link to="/credits" className="card-link">View Credits</Link>
          </div>
        </>
      )}
    </div>
  )
}

function PurchasedImage({ skuId }: { skuId: string }) {
  const accessToken = useSession().accessToken ?? ""
  const { data: sku } = useStoreSku(skuId, accessToken)
  if (sku === undefined)
    return null
  return <SkuImage sku={sku} alt={sku.name} />
}

function PurchaseSuccessContent({ skuId, purchaseId, rewardClaimId }: { skuId: string | null, purchaseId: string | null, rewardClaimId: string | null }) {
  const accessToken = useSession().accessToken ?? ""
  const [state, setState] = useState<PurchaseState>({ phase: "polling" })
  const [pollGeneration, setPollGeneration] = useState(0)
  const isClaim = rewardClaimId !== null

  useEffect(() => {
    if (!accessToken || (purchaseId === null && rewardClaimId === null))
      return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const startedAt = Date.now()

    const poll = async () => {
      if (cancelled)
        return
      try {
        if (rewardClaimId !== null) {
          // claims are fulfilled before the redirect, so one successful fetch
          // confirms — even with no matching licenses (e.g. a topup-only sku)
          const { licenses, products, skus } = await Api.getLicenses({ accessToken })
          if (cancelled)
            return
          setState({
            phase: "confirmed",
            order: null,
            licenses: licenses.filter((license) => license.refId === rewardClaimId),
            products,
            skus
          })
          return
        }
        const { orders } = await Api.listOrders({ purchaseId: purchaseId!, status: NfaOrderStatus.Paid, accessToken })
        if (cancelled)
          return
        const order = orders[0]
        if (order !== undefined) {
          const { licenses, products, skus } = await Api.getLicenses({ accessToken })
          if (cancelled)
            return
          setState({
            phase: "confirmed",
            order,
            licenses: licenses.filter((license) => license.refId === order.id),
            products,
            skus
          })
          return
        }
      } catch (err) {
        // Transient errors are expected right after the redirect — keep polling.
        console.error("Order poll failed:", err)
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setState({ phase: "timeout" })
        return
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS)
    }
    poll()

    return () => {
      cancelled = true
      if (timer !== undefined)
        clearTimeout(timer)
    }
  }, [purchaseId, rewardClaimId, accessToken, pollGeneration])

  const headerSku = state.phase === "confirmed" ? purchasedSku(state, skuId) : undefined

  return (
    <div className="page">
      <div className="dashboard-header">
        <h1 className="page-title" style={{ margin: 0 }}>{isClaim ? "Product claimed successfully!" : "Thank you for your purchase!"}</h1>
        {/* the purchased sku's logo (a single-product sku resolves to its
            product's logo server-side; no logo = the name as text) */}
        {state.phase === "confirmed" && headerSku !== undefined && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <strong><SkuName sku={headerSku} variant="section" sizes="160px" /></strong>
          </div>
        )}
      </div>

      {skuId && <PurchasedImage skuId={skuId} />}

      {purchaseId === null && rewardClaimId === null ? (
        // No purchaseId in the URL (old bookmark or hand-edited link): nothing
        // specific to poll for, so just point at the account pages.
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-detail">Your purchase is being processed — everything you bought will appear in your account shortly.</div>
          <div className="card-links">
            <Link to="/licenses" className="card-link">View Licenses</Link>
          </div>
        </div>
      ) : (
        <>
          {state.phase === "polling" && (
            <p className="empty-state"><span className="spinner" /> {isClaim ? "Finalizing your claim…" : "Finalizing your order…"}</p>
          )}

          {state.phase === "confirmed" && <ConfirmedCard state={state} skuId={skuId} />}

          {state.phase === "timeout" && (
            <div className="card" style={{ marginTop: 24 }}>
              <div className="card-title">{isClaim ? "Claim received" : "Payment received"}</div>
              <div className="card-detail">
                We're still processing your {isClaim ? "claim" : "order"} — your license will appear in your Licenses within a few minutes.
              </div>
              <div className="card-links">
                <Link to="/licenses" className="card-link">View Licenses</Link>
                <button
                  className="card-link"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  onClick={() => { setState({ phase: "polling" }); setPollGeneration(pollGeneration + 1) }}
                >
                  Keep waiting
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
